/**
 * @packageDocumentation
 * @module      EmcDisplayFacade.ts
 * @description Singleton - The class which the EMC UI page uses for data read and write, similar to a view model.
 * @copyright   Copyright © 2025 Siemens Healthineers. All rights reserved.
 */
import { EmcSection } from "./EmcEnums";
import EmcConsts from "./EmcConsts";
import { EmcDisplayDataManager } from "./EmcDisplayDataManager";
import { EMCDisplayMessageConsts } from "./EMCDisplayMessageConsts";
import ConsoleLogger from "../ConsoleLogger";
export class EmcDisplayFacade {
    constructor() {
        this.emcDisplayDataManager = new EmcDisplayDataManager();
        this.activeSection = EmcSection.systemStatus;
        // All systems
        this.allSystemsList = [];
        // Aggregated messages from all active systems
        this.messageText = '\n';
        //Signals
        this.emcSignals = this.emcDisplayDataManager.emcSignals;
        // Idle Monitor properties and methods
        this.isIdleMonitorSelected = true;
        this.isIdleMonitorRunning = false;
        this.idleRuntime = '0';
        this.startIdleMonitorTimeStamp = 0;
        // EMC Automation properties and methods
        this.isEmcAutomationSelected = false;
        this.isEMCAutomationRunning = false;
        this.emcAutomationRuntime = '0';
        this.startEmcAutomationTimestamp = 0;
        // Full system
        this.emcFullSystemSelected = true;
        this.armLock = false;
        // The systems currently active whose live data and messages need to be fetched periodically    
        this.activeSystemsList = [];
        this.prevActiveSystemsList = [];
        this.patientSystemCount = 0;
        this.controlSystemCount = 0;
        // Segments of live data 
        this.patientSubsystemsVersionData = this.emcDisplayDataManager.patientSubsystemsVersionData;
        this.controlSubsystemsVersionData = this.emcDisplayDataManager.controlSubsystemsVersionData;
        this.arm = this.emcDisplayDataManager.arm;
        this.drive = this.emcDisplayDataManager.drive;
        this.frame = this.emcDisplayDataManager.frame;
        this.csPdo = this.emcDisplayDataManager.csPdo;
        this.psPdo = this.emcDisplayDataManager.psPdo;
        this.latency = this.emcDisplayDataManager.latency;
        this.controlConnectionStatus = this.emcDisplayDataManager.controlConnectionStatus;
        this.communicationFaultCount = this.emcDisplayDataManager.communicationFaultCount;
        this.pim = this.emcDisplayDataManager.pim;
        this.csrc = this.emcDisplayDataManager.csrc;
        this.psrc = this.emcDisplayDataManager.psrc;
        // Other selection options    
        this.RotJog1 = true;
        this.RotJog2 = true;
        this.RotJog3 = true;
        this.RotJog4 = true;
        this.moveAllLinearMotors = false;
        this.emcReset = false;
        this.userInput = false;
        // Warning and fault counts
        this.warningCountDrive = 0;
        this.faultCountDrive = 0;
        this.warningCountArm = 0;
        this.faultCountArm = 0;
        this.warningCountFrame = 0;
        this.faultCountFrame = 0;
        this.warningCountPSRC = 0;
        this.faultCountPSRC = 0;
        this.warningCountPSIC = 0;
        this.faultCountPSIC = 0;
        this.warningCountPSUI = 0;
        this.faultCountPSUI = 0;
        this.warningCountPSSC = 0;
        this.faultCountPSSC = 0;
        this.warningCountCSRC = 0;
        this.faultCountCSRC = 0;
        this.warningCountCSIC = 0;
        this.faultCountCSIC = 0;
        this.warningCountCSUI = 0;
        this.faultCountCSUI = 0;
        this.warningCountCSSC = 0;
        this.faultCountCSSC = 0;
        this.warningCountPIM = 0;
        this.faultCountPIM = 0;
        this.warningFaultCountMap = this.createWarningFaultCountMap();
        this.interval = null;
        this.beepInterval = null;
    }
    static getEmcDisplayFacade() {
        if (null == EmcDisplayFacade.emcDisplayFacade) {
            EmcDisplayFacade.emcDisplayFacade = new EmcDisplayFacade();
        }
        return EmcDisplayFacade.emcDisplayFacade;
    }
    populateAllSystemList() {
        return this.emcDisplayDataManager.fetchAllSystemData()
            .then(() => {
            this.allSystemsList = this.emcDisplayDataManager.allSystemsList;
        });
    }
    setIdleMonitorSignal(mode) {
        this.emcSignals.IdleMonitor = mode;
        this.emcSignals.EmcMode = mode;
    }
    calculateIdleRunTime() {
        if (true == this.isIdleMonitorRunning) {
            let runTime = (Date.now() - this.startIdleMonitorTimeStamp) / 1000;
            this.idleRuntime = runTime.toString();
        }
        else
            this.idleRuntime = '0';
    }
    setDefaultTestDisableSignal() {
        this.emcSignals.TestDisable = true;
    }
    setEmcAutomationSignal(mode) {
        let testDisable = !mode;
        if ((testDisable != this.emcSignals.TestDisable))
            this.emcSignals.TestDisable = testDisable;
    }
    calculateEMCRunTime() {
        if (true == this.isEMCAutomationRunning) {
            let runTime = (Date.now() - this.startEmcAutomationTimestamp) / 1000;
            this.emcAutomationRuntime = runTime.toString();
        }
        else
            this.emcAutomationRuntime = '0';
    }
    // Common to both idle monitor and EMC automation
    calculateRunTimes() {
        this.calculateIdleRunTime();
        this.calculateEMCRunTime();
    }
    setFullSystemEmcSignal() {
        this.emcSignals = Object.assign(Object.assign({}, this.emcSignals), { FullSystem: true, ArmLockUnlock: false, DriveJog: false, RotJog1: false, RotJog2: false, RotJog3: false, RotJog4: false });
    }
    clearFullSystemEmcSignal() {
        this.emcSignals.FullSystem = false;
    }
    clearAllSubSystemEmcSignals() {
        this.emcSignals = Object.assign(Object.assign({}, this.emcSignals), { ArmLockUnlock: false, DriveJog: false, RotJog1: true, RotJog2: true, RotJog3: true, RotJog4: true });
    }
    setSubSystemSignal(subsystem) {
        switch (subsystem) {
            case 'MoveAllLinearMotors':
                this.emcSignals.DriveJog = this.moveAllLinearMotors;
                break;
            case 'ArmLock':
                this.emcSignals.ArmLockUnlock = this.armLock;
                break;
            case 'Mod1RotMotor':
                this.emcSignals.RotJog1 = this.RotJog1;
                break;
            case 'Mod2RotMotor':
                this.emcSignals.RotJog2 = this.RotJog2;
                break;
            case 'Mod3RotMotor':
                this.emcSignals.RotJog3 = this.RotJog3;
                break;
            case 'Mod4RotMotor':
                this.emcSignals.RotJog4 = this.RotJog4;
                break;
        }
    }
    toggleEmcResetSignal() {
        this.emcSignals.EmcReset = !this.emcSignals.EmcReset;
    }
    toggleDriveJogSignal() {
        this.emcSignals.DriveJog = !this.emcSignals.DriveJog;
    }
    setUserInputSignal(input) {
        this.emcSignals.UserInput = input;
    }
    resetFaultsAndWarningsCount() {
        this.warningFaultCountMap = this.createWarningFaultCountMap();
    }
    createWarningFaultCountMap() {
        let countMap = new Map();
        countMap.set("PSArm", { warnings: 0, faults: 0 });
        countMap.set("PSDrive", { warnings: 0, faults: 0 });
        countMap.set("PSFrame", { warnings: 0, faults: 0 });
        countMap.set("PSRC", { warnings: 0, faults: 0 });
        countMap.set("PSIC", { warnings: 0, faults: 0 });
        countMap.set("PSUI", { warnings: 0, faults: 0 });
        countMap.set("PSSC", { warnings: 0, faults: 0 });
        countMap.set("CSRC", { warnings: 0, faults: 0 });
        countMap.set("CSIC", { warnings: 0, faults: 0 });
        countMap.set("CSUI", { warnings: 0, faults: 0 });
        countMap.set("CSSC", { warnings: 0, faults: 0 });
        countMap.set("CSPIM", { warnings: 0, faults: 0 });
        return countMap;
    }
    // Class methods
    refresh() {
        this.worker = (() => {
            const setInterval = `
                setInterval(() => {
                postMessage("refresh");
                }, ${EmcConsts.REFRESH_INTERVAL});
            `;
            const blob = new Blob([setInterval], { type: "application/javascript" });
            return new Worker(URL.createObjectURL(blob));
        })();
        this.worker.onmessage = (event) => {
            if (event.data == "refresh") {
                this.emcDisplayDataManager.updateSignals(this.emcSignals);
                this.updateActiveSystems();
                this.fetchActiveSystemDataAndMessages();
                this.getWarningsAndFaultCounts();
            }
        };
    }
    addMessageToMessageDisplay(message) {
        this.messageText = message + '\n' + this.messageText;
    }
    exportMessages() {
        const faultdata = this.messageText;
        const csvData = EmcDisplayFacade.convertToCSV(faultdata);
        const blob = new Blob([csvData], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'fault_messages.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }
    fetchActiveSystemDataAndMessages() {
        this.resetFaultsAndWarningsCount();
        this.activeSystemsList.forEach(system => {
            this.latency = this.emcDisplayDataManager.latency;
            this.controlConnectionStatus = this.emcDisplayDataManager.controlConnectionStatus;
            this.communicationFaultCount = this.emcDisplayDataManager.communicationFaultCount;
            this.csPdo = this.emcDisplayDataManager.csPdo;
            this.psPdo = this.emcDisplayDataManager.psPdo;
            this.pim = this.emcDisplayDataManager.pim;
            this.arm = this.emcDisplayDataManager.arm;
            this.drive = this.emcDisplayDataManager.drive;
            this.frame = this.emcDisplayDataManager.frame;
            this.csrc = this.emcDisplayDataManager.csrc;
            this.psrc = this.emcDisplayDataManager.psrc;
            this.patientSubsystemsVersionData = this.emcDisplayDataManager.patientSubsystemsVersionData;
            this.controlSubsystemsVersionData = this.emcDisplayDataManager.controlSubsystemsVersionData;
            ConsoleLogger.log("Inside Display facade's fetchActiveSystemDataAndMessages");
            let emcPacket = this.emcDisplayDataManager.fetchActiveSystemData(system.systemId);
            if (undefined != emcPacket) {
                if (emcPacket.isControlSide == true)
                    this.appendMessages(system.systemId, emcPacket, "Control");
                else
                    this.appendMessages(system.systemId, emcPacket, "Patient");
                this.populateWarningsAndFaultCounts(emcPacket);
            }
        });
    }
    updateActiveSystems() {
        if (this.prevActiveSystemsList.length > this.activeSystemsList.length) {
            let activeSystemIDs = this.activeSystemsList.map(system => system.systemId);
            this.prevActiveSystemsList.forEach(removedActiveSystem => {
                if (false == activeSystemIDs.includes(removedActiveSystem.systemId)) {
                    this.emcDisplayDataManager.removeInactiveSystem(removedActiveSystem.systemId);
                }
            });
        }
        this.activeSystemsList.forEach(system => {
            this.emcDisplayDataManager.updateActiveSystem(system.systemId);
        });
        this.prevActiveSystemsList = Array.from(this.activeSystemsList);
    }
    appendMessages(systemId, emcPacket, PS_CS) {
        emcPacket.currentFaultMessages.forEach(faultMessage => {
            let message = "";
            message += EmcDisplayFacade.parsePacketDateToUSDateTimeFormat(faultMessage.timestamp) + " ";
            message += systemId + " ";
            message += PS_CS + " ";
            message += faultMessage.severity + " ";
            message += faultMessage.subsystem + ": ";
            message += faultMessage.description;
            this.addMessageToMessageDisplay(message);
        });
        emcPacket.removedFaultMessages.forEach(faultMessage => {
            let message = "";
            message += EmcDisplayFacade.parsePacketDateToUSDateTimeFormat(faultMessage.timestamp) + " ";
            message += systemId + " ";
            message += PS_CS + " ";
            message += EMCDisplayMessageConsts.MsgCleared + " ";
            message += faultMessage.severity + " ";
            message += faultMessage.subsystem + ": ";
            message += faultMessage.description;
            this.addMessageToMessageDisplay(message);
        });
        emcPacket.stateChangeMessages.forEach(stateMessage => {
            let message = "";
            message += EmcDisplayFacade.parsePacketDateToUSDateTimeFormat(stateMessage.timestamp) + " ";
            message += systemId + " ";
            message += stateMessage.parameter + ": ";
            message += stateMessage.description;
            this.addMessageToMessageDisplay(message);
        });
    }
    populateWarningsAndFaultCounts(emcPacket) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z;
        this.warningFaultCountMap.get("PSArm").warnings += (_a = emcPacket.faultCounts.psArm) === null || _a === void 0 ? void 0 : _a.warnings;
        this.warningFaultCountMap.get("PSArm").faults += (_b = emcPacket.faultCounts.psArm) === null || _b === void 0 ? void 0 : _b.faults;
        this.warningFaultCountMap.get("PSDrive").warnings += (_c = emcPacket.faultCounts.psDrive) === null || _c === void 0 ? void 0 : _c.warnings;
        this.warningFaultCountMap.get("PSDrive").faults += (_d = emcPacket.faultCounts.psDrive) === null || _d === void 0 ? void 0 : _d.faults;
        this.warningFaultCountMap.get("PSFrame").warnings += (_e = emcPacket.faultCounts.psFrame) === null || _e === void 0 ? void 0 : _e.warnings;
        this.warningFaultCountMap.get("PSFrame").faults += (_f = emcPacket.faultCounts.psFrame) === null || _f === void 0 ? void 0 : _f.faults;
        this.warningFaultCountMap.get("PSRC").warnings += (_g = emcPacket.faultCounts.psRc) === null || _g === void 0 ? void 0 : _g.warnings;
        this.warningFaultCountMap.get("PSRC").faults += (_h = emcPacket.faultCounts.psRc) === null || _h === void 0 ? void 0 : _h.faults;
        this.warningFaultCountMap.get("PSIC").warnings += (_j = emcPacket.faultCounts.psIc) === null || _j === void 0 ? void 0 : _j.warnings;
        this.warningFaultCountMap.get("PSIC").faults += (_k = emcPacket.faultCounts.psIc) === null || _k === void 0 ? void 0 : _k.faults;
        this.warningFaultCountMap.get("PSUI").warnings += (_l = emcPacket.faultCounts.psUi) === null || _l === void 0 ? void 0 : _l.warnings;
        this.warningFaultCountMap.get("PSUI").faults += (_m = emcPacket.faultCounts.psUi) === null || _m === void 0 ? void 0 : _m.faults;
        this.warningFaultCountMap.get("PSSC").warnings += (_o = emcPacket.faultCounts.psSc) === null || _o === void 0 ? void 0 : _o.warnings;
        this.warningFaultCountMap.get("PSSC").faults += (_p = emcPacket.faultCounts.psSc) === null || _p === void 0 ? void 0 : _p.faults;
        this.warningFaultCountMap.get("CSRC").warnings += (_q = emcPacket.faultCounts.csRc) === null || _q === void 0 ? void 0 : _q.warnings;
        this.warningFaultCountMap.get("CSRC").faults += (_r = emcPacket.faultCounts.csRc) === null || _r === void 0 ? void 0 : _r.faults;
        this.warningFaultCountMap.get("CSIC").warnings += (_s = emcPacket.faultCounts.csIc) === null || _s === void 0 ? void 0 : _s.warnings;
        this.warningFaultCountMap.get("CSIC").faults += (_t = emcPacket.faultCounts.csIc) === null || _t === void 0 ? void 0 : _t.faults;
        this.warningFaultCountMap.get("CSUI").warnings += (_u = emcPacket.faultCounts.csUi) === null || _u === void 0 ? void 0 : _u.warnings;
        this.warningFaultCountMap.get("CSUI").faults += (_v = emcPacket.faultCounts.csUi) === null || _v === void 0 ? void 0 : _v.faults;
        this.warningFaultCountMap.get("CSSC").warnings += (_w = emcPacket.faultCounts.csSc) === null || _w === void 0 ? void 0 : _w.warnings;
        this.warningFaultCountMap.get("CSSC").faults += (_x = emcPacket.faultCounts.csSc) === null || _x === void 0 ? void 0 : _x.faults;
        this.warningFaultCountMap.get("CSPIM").warnings += (_y = emcPacket.faultCounts.csPim) === null || _y === void 0 ? void 0 : _y.warnings;
        this.warningFaultCountMap.get("CSPIM").faults += (_z = emcPacket.faultCounts.csPim) === null || _z === void 0 ? void 0 : _z.faults;
    }
    getWarningsAndFaultCounts() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z;
        this.warningCountArm = (_a = this.warningFaultCountMap.get("PSArm")) === null || _a === void 0 ? void 0 : _a.warnings;
        this.faultCountArm = (_b = this.warningFaultCountMap.get("PSArm")) === null || _b === void 0 ? void 0 : _b.faults;
        this.warningCountDrive = (_c = this.warningFaultCountMap.get("PSDrive")) === null || _c === void 0 ? void 0 : _c.warnings;
        this.faultCountDrive = (_d = this.warningFaultCountMap.get("PSDrive")) === null || _d === void 0 ? void 0 : _d.faults;
        this.warningCountFrame = (_e = this.warningFaultCountMap.get("PSFrame")) === null || _e === void 0 ? void 0 : _e.warnings;
        this.faultCountFrame = (_f = this.warningFaultCountMap.get("PSFrame")) === null || _f === void 0 ? void 0 : _f.faults;
        this.warningCountPSRC = (_g = this.warningFaultCountMap.get("PSRC")) === null || _g === void 0 ? void 0 : _g.warnings;
        this.faultCountPSRC = (_h = this.warningFaultCountMap.get("PSRC")) === null || _h === void 0 ? void 0 : _h.faults;
        this.warningCountPSIC = (_j = this.warningFaultCountMap.get("PSIC")) === null || _j === void 0 ? void 0 : _j.warnings;
        this.faultCountPSIC = (_k = this.warningFaultCountMap.get("PSIC")) === null || _k === void 0 ? void 0 : _k.faults;
        this.warningCountPSUI = (_l = this.warningFaultCountMap.get("PSUI")) === null || _l === void 0 ? void 0 : _l.warnings;
        this.faultCountPSUI = (_m = this.warningFaultCountMap.get("PSUI")) === null || _m === void 0 ? void 0 : _m.faults;
        this.warningCountPSSC = (_o = this.warningFaultCountMap.get("PSSC")) === null || _o === void 0 ? void 0 : _o.warnings;
        this.faultCountPSSC = (_p = this.warningFaultCountMap.get("PSSC")) === null || _p === void 0 ? void 0 : _p.faults;
        this.warningCountCSRC = (_q = this.warningFaultCountMap.get("CSRC")) === null || _q === void 0 ? void 0 : _q.warnings;
        this.faultCountCSRC = (_r = this.warningFaultCountMap.get("CSRC")) === null || _r === void 0 ? void 0 : _r.faults;
        this.warningCountCSIC = (_s = this.warningFaultCountMap.get("CSIC")) === null || _s === void 0 ? void 0 : _s.warnings;
        this.faultCountCSIC = (_t = this.warningFaultCountMap.get("CSIC")) === null || _t === void 0 ? void 0 : _t.faults;
        this.warningCountCSUI = (_u = this.warningFaultCountMap.get("CSUI")) === null || _u === void 0 ? void 0 : _u.warnings;
        this.faultCountCSUI = (_v = this.warningFaultCountMap.get("CSUI")) === null || _v === void 0 ? void 0 : _v.faults;
        this.warningCountCSSC = (_w = this.warningFaultCountMap.get("CSSC")) === null || _w === void 0 ? void 0 : _w.warnings;
        this.faultCountCSSC = (_x = this.warningFaultCountMap.get("CSSC")) === null || _x === void 0 ? void 0 : _x.faults;
        this.warningCountPIM = (_y = this.warningFaultCountMap.get("CSPIM")) === null || _y === void 0 ? void 0 : _y.warnings;
        this.faultCountPIM = (_z = this.warningFaultCountMap.get("CSPIM")) === null || _z === void 0 ? void 0 : _z.faults;
    }
    static convertToCSV(data) {
        return data.split('\n').map(row => row.replace(/,/g, ' ')).join('\n');
    }
    static parsePacketDateToUSDateTimeFormat(ddMMYYYY_HHmmss) {
        const [day, month, year, time] = ddMMYYYY_HHmmss.split(".");
        const [hour, minute, second] = time.split(":").map(Number);
        const date = new Date(Number(year), Number(month) - 1, Number(day), hour, minute, second);
        return date.toLocaleString("en-US");
    }
    startAudibleTone() {
        this.beepInterval = setInterval(this.playAudibleTone, 5000); // every 5 sec
    }
    stopAudibleTone() {
        clearInterval(this.beepInterval);
        this.beepInterval = null;
    }
    playAudibleTone() {
        let audioContext = new AudioContext();
        const oscillator = audioContext.createOscillator();
        const gain = audioContext.createGain();
        oscillator.type = "square";
        oscillator.frequency.value = 1000;
        gain.gain.value = 0.1;
        oscillator.connect(gain);
        gain.connect(audioContext.destination);
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.1);
    }
}
//# sourceMappingURL=EmcDisplayFacade.js.map